// ============= Imports =============

import { tokenManager } from '@tw/tw-component-library';
import { AuthContextProps } from '../../../contexts/AuthContext';
import { createLogger } from '../../../utils/logger';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Permissions/workflow';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = {};

// ============= Logger =============
const logger = createLogger('InvolvementPermissions');

// ============= Types =============

interface WorkflowType {
  workflowId: number;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser?: string | null;
}

// ============= Utility Functions =============
/**
 * Constructs filter parameters for API query
 * @param searchValue Search term to filter users
 * @returns Formatted filter string
 */
// const constructFilterParams = (searchValue: string): string => {
//   return [
//     `startswith(givenName,'${searchValue}')`,
//     `startswith(surname,'${searchValue}')`,
//     `startswith(userPrincipalName,'${searchValue}')`,
//   ].join(' or ');
// };

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

// ============= Main Function =============
/**
 * Fetches users from Microsoft API based on search criteria
 * @param getAccessToken Function to acquire access token
 * @param searchValue Search string to filter users
 * @returns Promise with API response containing user data or error
 */
export const involvementPermissions = async ({
  workflowId,
  getAccessToken,
  spoofedUser,
}: WorkflowType) => {
  try {
    logger.debug('Fetching API data', { workflowId });

    // Token acquisition with required scopes

    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workflowId}/involvement`;
    const convertURI = encodeURI(url);
    logger.debug('Making request to API', { url: convertURI });

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token, spoofedUser || null),
    });

    // Response handling
    if (!response.ok) {
      logger.error('HTTP error occurred', {
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
      });
      // Try to get the actual error message from the response
      let errorMessage = `HTTP error! status: ${response.status}`;

      // Check content type to determine how to parse the response
      const contentType = response.headers.get('content-type');
      logger.debug('Response content type', { contentType });

      if (contentType && contentType.includes('application/json')) {
        // Handle JSON response
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // If JSON parsing fails, use status text
          errorMessage =
            response.statusText || `HTTP error! status: ${response.status}`;
        }
      } else {
        // Handle plain text response (like the one you're seeing)
        try {
          const responseText = await response.text();
          logger.debug('Response text', { responseText });
          if (responseText && responseText.trim()) {
            errorMessage = responseText.trim();
          }
        } catch {
          // If text parsing fails, use status text
          errorMessage =
            response.statusText || `HTTP error! status: ${response.status}`;
        }
      }

      logger.debug('Final error message', { errorMessage });
      throw new Error(errorMessage);
    }

    const data = await response.json();
    logger.debug('API data fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    logger.error('Error fetching API data', { error });
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
